/**
 * CAPTCHA Component JavaScript
 * Handles visual and audio CAPTCHA functionality
 */

class CaptchaComponent {
  constructor(container) {
    this.container = container;
    this.currentMode = 'visual';
    this.currentAnswer = '';
    this.isValidated = false;
    this.speechSynthesis = window.speechSynthesis;
    this.currentUtterance = null;
    
    this.init();
  }

  init() {
    this.bindEvents();
    // Only generate captcha for the current mode (visual by default)
    if (this.currentMode === 'visual') {
      this.generateVisualCaptcha();
    } else {
      this.generateAudioCaptcha();
    }
  }

  bindEvents() {
    // Mode toggle
    this.container.querySelectorAll('[data-captcha-mode]').forEach(btn => {
      btn.addEventListener('click', (e) => this.switchMode(e.target.dataset.captchaMode));
    });

    // Visual captcha
    this.container.querySelector('[data-captcha-refresh]')?.addEventListener('click', () => this.generateVisualCaptcha());
    this.container.querySelector('[data-captcha-canvas]')?.addEventListener('click', () => this.generateVisualCaptcha());

    // Audio captcha
    this.container.querySelector('[data-captcha-play]')?.addEventListener('click', () => this.playAudio());
    this.container.querySelector('[data-captcha-stop]')?.addEventListener('click', () => this.stopAudio());
    this.container.querySelector('[data-captcha-audio-refresh]')?.addEventListener('click', () => this.generateAudioCaptcha());

    // Validation
    this.container.querySelector('[data-captcha-validate]')?.addEventListener('click', () => this.validateAnswer());
    this.container.querySelector('[data-captcha-input]')?.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') this.validateAnswer();
    });
  }

  switchMode(mode) {
    this.currentMode = mode;
    this.resetValidation();
    
    // Update toggle buttons
    this.container.querySelectorAll('[data-captcha-mode]').forEach(btn => {
      btn.classList.toggle('active', btn.dataset.captchaMode === mode);
      btn.setAttribute('aria-pressed', btn.dataset.captchaMode === mode);
    });

    // Show/hide content
    this.container.querySelector('[data-captcha-visual]').classList.toggle('hidden', mode !== 'visual');
    this.container.querySelector('[data-captcha-audio]').classList.toggle('hidden', mode !== 'audio');

    // Stop any playing audio
    this.stopAudio();
  }

  generateVisualCaptcha() {
    const canvas = this.container.querySelector('[data-captcha-canvas]');
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;

    // Clear canvas
    ctx.clearRect(0, 0, width, height);

    // Generate random text
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let text = '';
    for (let i = 0; i < 5; i++) {
      text += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    this.currentAnswer = text;

    // Draw background
    ctx.fillStyle = '#f8f9fa';
    ctx.fillRect(0, 0, width, height);

    // Draw text with distortion
    ctx.font = 'bold 24px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      const x = (width / text.length) * (i + 0.5);
      const y = height / 2 + (Math.random() - 0.5) * 10;

      // Random color
      const hue = Math.random() * 360;
      ctx.fillStyle = `hsl(${hue}, 70%, 40%)`;

      // Random rotation
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate((Math.random() - 0.5) * 0.5);
      ctx.fillText(char, 0, 0);
      ctx.restore();
    }

    // Add noise lines
    ctx.strokeStyle = '#dee2e6';
    ctx.lineWidth = 1;
    for (let i = 0; i < 5; i++) {
      ctx.beginPath();
      ctx.moveTo(Math.random() * width, Math.random() * height);
      ctx.lineTo(Math.random() * width, Math.random() * height);
      ctx.stroke();
    }

    // Add noise dots
    ctx.fillStyle = '#dee2e6';
    for (let i = 0; i < 20; i++) {
      ctx.beginPath();
      ctx.arc(Math.random() * width, Math.random() * height, 1, 0, 2 * Math.PI);
      ctx.fill();
    }
  }

  generateAudioCaptcha() {
    // Generate random digits for audio
    const digits = [];
    for (let i = 0; i < 6; i++) {
      digits.push(Math.floor(Math.random() * 10));
    }
    this.currentAnswer = digits.join('');
  }

  playAudio() {
    if (!this.speechSynthesis) {
      alert('Trình duyệt này không hỗ trợ phát âm thanh.');
      return;
    }

    this.stopAudio();

    const playBtn = this.container.querySelector('[data-captcha-play]');
    const stopBtn = this.container.querySelector('[data-captcha-stop]');

    playBtn.classList.add('hidden');
    stopBtn.classList.remove('hidden');

    const text = this.currentAnswer.split('').join(' ');
    this.currentUtterance = new SpeechSynthesisUtterance(text);
    this.currentUtterance.rate = 0.7;
    this.currentUtterance.pitch = 1;
    this.currentUtterance.volume = 1;

    this.currentUtterance.onend = () => {
      playBtn.classList.remove('hidden');
      stopBtn.classList.add('hidden');
    };

    this.currentUtterance.onerror = () => {
      playBtn.classList.remove('hidden');
      stopBtn.classList.add('hidden');
      alert('Lỗi phát âm thanh. Vui lòng thử lại.');
    };

    this.speechSynthesis.speak(this.currentUtterance);
  }

  stopAudio() {
    if (this.currentUtterance) {
      this.speechSynthesis.cancel();
      this.currentUtterance = null;
    }

    const playBtn = this.container.querySelector('[data-captcha-play]');
    const stopBtn = this.container.querySelector('[data-captcha-stop]');

    playBtn.classList.remove('hidden');
    stopBtn.classList.add('hidden');
  }

  validateAnswer() {
    const input = this.container.querySelector('[data-captcha-input]');
    const userAnswer = input.value.trim().toUpperCase();
    const isValid = userAnswer === this.currentAnswer;

    this.resetValidation();

    if (isValid) {
      this.showSuccess();
      this.isValidated = true;
      this.container.querySelector('[data-captcha-valid]').value = 'true';
    } else {
      this.showError();
      this.isValidated = false;
      this.container.querySelector('[data-captcha-valid]').value = 'false';
    }

    return isValid;
  }

  showError() {
    const input = this.container.querySelector('[data-captcha-input]');
    const error = this.container.querySelector('[data-captcha-error]');

    input.classList.add('error');
    error.classList.remove('hidden');
  }

  showSuccess() {
    const input = this.container.querySelector('[data-captcha-input]');
    const success = this.container.querySelector('[data-captcha-success]');

    input.classList.add('success');
    success.classList.remove('hidden');
  }

  resetValidation() {
    const input = this.container.querySelector('[data-captcha-input]');
    const error = this.container.querySelector('[data-captcha-error]');
    const success = this.container.querySelector('[data-captcha-success]');

    input.classList.remove('error', 'success');
    error.classList.add('hidden');
    success.classList.add('hidden');
    input.value = '';
  }

  // Public method to check if captcha is validated
  isValid() {
    return this.isValidated;
  }

  // Public method to reset captcha
  reset() {
    this.resetValidation();
    this.isValidated = false;
    this.container.querySelector('[data-captcha-valid]').value = 'false';
    
    if (this.currentMode === 'visual') {
      this.generateVisualCaptcha();
    } else {
      this.generateAudioCaptcha();
    }
  }
}

// Initialize captcha components when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  document.querySelectorAll('[data-captcha-container]').forEach(container => {
    new CaptchaComponent(container);
  });
});

// Form validation helper
window.validateCaptcha = function(form) {
  const captchaContainer = form.querySelector('[data-captcha-container]');
  if (!captchaContainer) return true;

  const captchaComponent = captchaContainer.captchaInstance;
  if (!captchaComponent) return true;

  return captchaComponent.isValid();
};